import { PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { buildFilters, MeetingCreationOrigin, MeetingOutputDTO, MeetingSortMethod, SORT_ORDER } from "@kalyzee/kast-web-sdk/http";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ShareMeetingPopup } from "../../../../components/common/ShareMeetingPopup";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";
import { AgendaPanel, EventData, getMeetingStatusLabel, isMeetingLive } from "../../../../components/utils/TimeEventsAgenda";
import agendaStyles from "../../../../components/utils/TimeEventsAgenda.module.css";
import { ErrorView } from "../../../../components/utils/ErrorView";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { useDelayedState } from "../../../../hooks/states";

/** How many meetings the tab shows. */
const DISPLAYED_COUNT = 5;

/**
 * No room sort: most instant meetings have none. Hoisted because the panel resets its dropdown
 * whenever it receives a new options array.
 */
const SORT_METHODS = ["date", "name"];

/** Value of the `tab` query that selects this view — kept when opening a replay. */
const INSTANT_TAB_QUERY_VALUE = "instant";

/**
 * Whether the row may offer its replays. Nothing is playable while the meeting runs, and the
 * listing carries no VOD: `vodsCount` when the API returns it, the recording flag otherwise.
 */
const hasReplays = (meeting: MeetingOutputDTO): boolean => {
  if (isMeetingLive(meeting.state)) return false;
  if (meeting.vodsCount !== undefined) return meeting.vodsCount > 0;
  return meeting.record === true;
};

/**
 * Projects a meeting onto the row model the agenda panel renders. A meeting has no planned range,
 * so it carries no `endDatetime` and states its status explicitly instead of having it derived
 * from a time window.
 */
const toEventData = (meeting: MeetingOutputDTO): EventData => {
  const live = isMeetingLive(meeting.state);
  const createdAt = meeting.createdAt ? new Date(meeting.createdAt) : undefined;
  return {
    id: meeting._id,
    title: meeting.name || "Meeting sans nom",
    // Room and sub-organization feed the panel filters; the header keeps the join code.
    roomId: meeting.room?._id,
    roomName: meeting.room?.name,
    subOrganizationId: meeting.room?.subOrganization?._id,
    subOrganizationName: meeting.room?.subOrganization?.name,
    header: [meeting.room?.subOrganization?.name, meeting.shortId ? `Code ${meeting.shortId}` : undefined].filter(Boolean).join(" · ") || undefined,
    startDatetime: createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt : new Date(0),
    status: live ? "ongoing" : "finished",
    statusLabel: getMeetingStatusLabel(meeting.state),
    actionLabel: live ? "Rejoindre" : "Rouvrir",
  };
};

/**
 * The instant meetings the current user took part in — created or merely attended — most recent
 * first.
 *
 * `GET /meeting/me` is already scoped to the caller (the meetings they attended, their own
 * creations included), so the only filter left narrows it to the ones started from the instant
 * popup. Older meetings predate that marker and will not appear.
 */
export const InstantMeetingsAgenda = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const sdkCtx = useSdkContext();

  const defaultResult = useMemo(
    () => ({ meetings: undefined as MeetingOutputDTO[] | undefined, error: undefined as any }),
    [],
  );
  const [result, setResult] = useDelayedState(defaultResult);
  const [sharedMeetingId, setSharedMeetingId] = useState<string>();

  const loadMeetings = useCallback(() => {
    sdkCtx.client
      .getHttpClient()
      .meeting.getMyMeetings({
        limit: DISPLAYED_COUNT,
        filters: buildFilters([
          { field: "creationOrigin", operator: "eq", value: MeetingCreationOrigin.INSTANT },
        ]),
        sortMethod: MeetingSortMethod.CREATED_AT,
        sortOrder: SORT_ORDER.DESCENDING,
      })
      .then((r) => {
        setResult({ meetings: r.data ?? [], error: undefined }, 500);
      })
      .catch((error) => {
        setResult({ meetings: undefined, error }, 500);
      });
  }, [sdkCtx.client, setResult]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const onJoinEvent = useCallback((meetingId: string) => {
    pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: meetingId });
  }, [pageContextRef]);

  // Push (not replace): the replay opens as an overlay above the list via `?vodId=`, so the
  // browser Back button closes it. `tab` rides along so closing it comes back to this tab.
  const onWatchReplays = useCallback((meetingId: string) => {
    pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, {
      queries: {
        [getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)]: meetingId,
        [getGlobalQueryLabel(NavigateGlobalQuery.LIST_TAB)]: INSTANT_TAB_QUERY_VALUE,
      },
    });
  }, [pageContextRef]);

  const events = useMemo(() => result.meetings?.map(toEventData), [result.meetings]);

  const renderExtraAction = useCallback((meetingId: string) => {
    const meeting = result.meetings?.find(m => m._id === meetingId);
    return (
      <>
        {!!meeting && hasReplays(meeting) && (
          <PrimaryButton
            className={agendaStyles.iconBtn}
            onClick={() => onWatchReplays(meetingId)}
            nativeProps={{ title: "Voir les replays" }}
          >
            <ReactIcons.PlayScreens />
          </PrimaryButton>
        )}
        <PrimaryButton
          className={agendaStyles.iconBtn}
          onClick={() => setSharedMeetingId(meetingId)}
          nativeProps={{ title: "Partager le meeting" }}
        >
          <ReactIcons.QRCode />
        </PrimaryButton>
      </>
    );
  }, [result.meetings, onWatchReplays]);

  const sharedMeeting = useMemo(
    () => result.meetings?.find(meeting => meeting._id === sharedMeetingId),
    [result.meetings, sharedMeetingId],
  );

  if (result.error) return <ErrorView retry={loadMeetings} />;

  return (
    <>
      <AgendaPanel
        events={events}
        placeholdersNb={DISPLAYED_COUNT}
        mode="instant"
        emptyLabel="Aucun meeting instantané"
        sortMethods={SORT_METHODS}
        onJoinEvent={onJoinEvent}
        renderExtraAction={renderExtraAction}
      />
      <ShareMeetingPopup
        visible={!!sharedMeeting}
        shortId={sharedMeeting?.shortId}
        meetingName={sharedMeeting?.name}
        onClose={() => setSharedMeetingId(undefined)}
      />
    </>
  );
};
